import * as React from 'react';
import { connect } from 'react-redux';
import { AppState } from '../../store';
import { bindActionCreators, Dispatch } from 'redux';
import * as DashboardStore from '../../store/Dashboard';
import {  Col, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import { Dashboard } from '../../types/Dashboard/Dashboard';
import { history } from '../../store/configureStore';
import { User } from 'oidc-client';
import ReactTimeAgo from 'react-time-ago';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLockAlt, faUnlockAlt } from '@fortawesome/pro-solid-svg-icons';
import NoPermissions from '../Common/NoPermissions/NoPermissions';


type DashboardTableProps = {
    dispatch: Dispatch;
    dashboardState: DashboardStore.DashboardState;
    user: User;
    actions:  typeof DashboardStore.actionCreators;
}

interface DashboardTableState {
    showMatchTenantModal: boolean;
    showUnlinkTenantModal: boolean;
    dashboard: Dashboard | undefined;
}

class DashboardsTable extends React.Component<DashboardTableProps, DashboardTableState> {
    public constructor(props: DashboardTableProps) {
        super(props);
        this.state = {
            showMatchTenantModal: false,
            showUnlinkTenantModal: false,
            dashboard: undefined,
        };
    }

    public componentDidMount() {
        this.ensureDataFetched();
    }

    public componentDidUpdate() {
        this.ensureDataFetched();
    }

    private ensureDataFetched() {
        this.props.actions.requestDashboards();
    }


    render() {
        const NoDataComponent: React.FunctionComponent = (): React.ReactElement => {
            if(this.props.dashboardState.isLoadedData){
                return (
                    <div className="d-flex justify-content-center mt-3" data-cy="bootstrap-table-loaded">
                        <p className="text-center">No dashboards were found.</p>
                    </div>
                );
            }
            else{
                return (
                    <div className="d-flex justify-content-center mt-3">
                        <Spinner animation="border" variant="primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </Spinner>
                    </div>
                );
            }
        };

        if (!this.props.dashboardState.hasPermissionsToList) {
            return (
                <NoPermissions />
            );
        }

        const { SearchBar } = Search;

        const columns = [
            {
                dataField: 'displayName',
                text: 'Name',
                sort: true,
            },
            {
                dataField: 'id',
                text: 'Dashboard ID',
                sort: true,
            },
            {
                dataField: 'groupName',
                text: 'Group',
                sort: true,
            },
            {
                dataField: 'lastDatasetRefreshHistoryDate',
                text: 'Refresh Status',
                sort: true,
                formatter: (cell: string, row: Dashboard) => {
                    return !cell ? "" : <ReactTimeAgo date={new Date(cell)} locale="en-US"/>
                },
            },
            {
                dataField: 'isStale',
                text: 'Is Stale?',
                sort: true,
                formatter: (cell: boolean, row: Dashboard) => {
                    return row.isStale ? "Yes" : "";
                },
            },
            {
                dataField: 'isAccessControlEntryRequired',
                text: 'Authorization',
                sort: true,
                formatter: (cell: string, row: Dashboard) => {
                    return row.isAccessControlEntryRequired ? 
                        <OverlayTrigger
                         placement='bottom'
                         overlay={
                            <Tooltip>Dashboard has access controls applied.</Tooltip>
                         }
                        >
                        <span style={{"color": "darkgreen"}}><FontAwesomeIcon icon={faLockAlt}/></span>
                        </OverlayTrigger>
                         : 
                        <OverlayTrigger
                         placement='bottom'
                         overlay={
                            <Tooltip>Dashboard is visible to everyone.</Tooltip>
                         }
                        >
                        <span style={{"color": "darkred"}}><FontAwesomeIcon icon={faUnlockAlt}/></span>
                        </OverlayTrigger>
                        
                }

            },
            {
                dataField: 'tenantDomain',
                text: 'Tenant',
                sort: true,
            }
        ];

        const rowEvents: Partial<any> = {
            onClick: (e: React.MouseEvent<HTMLButtonElement>, row: Dashboard, rowIndex: number) => {
                e.preventDefault();
                e.stopPropagation();
                let target = e.target as HTMLButtonElement;
                // Only goto tenant details when it's not a link click
                if(!target.classList.contains('btn-link')){
                    history.push(`/dashboards/${row.id}`);
                }
            },
        };

        return (
            <React.Fragment>
                <ToolkitProvider
                    keyField="id"
                    search
                    data={ this.props.dashboardState.data } 
                    columns={ columns } 
                    >
                    {
                        props => (
                        <React.Fragment>
                            <Row>
                                <Col>
                                    <SearchBar { ...props.searchProps } />
                                </Col>
                            </Row>
                            <BootstrapTable
                                { ...props.baseProps }
                                id="dashboardTable"
                                classes='clickable'
                                striped
                                hover
                                bordered={false}
                                defaultSorted={ [{ dataField: 'name', order: 'asc' }] }
                                pagination={ paginationFactory({sizePerPage: 25}) }
                                noDataIndication={ () => <NoDataComponent /> }
                                rowEvents={ rowEvents }
                            />
                        </React.Fragment>
                        )
                    }
                </ToolkitProvider>

            </React.Fragment>
        );
    }
}

function mapStateToProps(state: AppState) {
    return {
        dashboardState: state.dashboard,
        user: state.oidc.user,
    };
}

export default connect(
    mapStateToProps,
    (dispatch) => {
        return {
            dispatch: dispatch,
            actions: bindActionCreators(Object.assign(
                {}, 
                DashboardStore.actionCreators,
            ), dispatch )
        }
    }
)(DashboardsTable as any);